import React, { useEffect, useState } from "react";
import { getDonorPerfectCodes } from "../../../../utils/fetch";
import ApiKeyInput from "../common/apiKeyInput";
import { ICodeModel } from "../../../../types/services/DonorPerfectTypes";
import { Segment } from "../common/segmentsPills/types";
import { maskApiKey } from "./utils";
import styles from "./styles.scss";

const parseSegments = (from: Array<ICodeModel>): Array<Segment> =>
  from.map((segment: ICodeModel) => ({
    id: segment.code,
    name: segment.description,
  }));

export interface ApiKeySettingsStepProps {
  currentApiKey: string;
  charityId: string;
  integrationsServiceUrl: string;
  serviceAccessToken: string;
  onApiKeyReady: (
    payload: {
      apiKey: string;
      flags: Segment[];
      types: Segment[];
      generalLedgers: ICodeModel[];
      campaigns: ICodeModel[];
      thankYouLetters: ICodeModel[];
    },
    supportsExtraGiftFields: boolean
  ) => void;
  onValidationChange: (valid: boolean) => void;
}

const ApiKeySettingsStep = ({
  currentApiKey,
  charityId,
  integrationsServiceUrl,
  serviceAccessToken,
  onApiKeyReady,
  onValidationChange,
}: ApiKeySettingsStepProps) => {
  const [apiKeyValue, setApiKeyValue] = useState<string>("");
  const [isValid, setIsValid] = useState<boolean | undefined>(undefined);
  const [canConnect, setCanConnect] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setApiKeyValue("");
    setIsValid(undefined);
    setCanConnect(false);
    setFeedback("");
    onValidationChange(true);
  }, [currentApiKey, onValidationChange]);

  const handleApiKeyChange = (value: string) => {
    setApiKeyValue(value);
    setIsValid(undefined);
    if (!value) {
      onValidationChange(true);
      return;
    }
    onValidationChange(false);
  };

  const verifyApiKey = () => {
    setIsLoading(true);
    getDonorPerfectCodes(
      integrationsServiceUrl,
      charityId,
      apiKeyValue,
      serviceAccessToken
    )
      .then(response => {
        if (response.donorTypes && response.flags) {
          onApiKeyReady(
            {
              apiKey: apiKeyValue,
              flags: parseSegments(response.flags),
              types: parseSegments(response.donorTypes),
              generalLedgers: response.generalLedgers || [],
              campaigns: response.campaigns || [],
              thankYouLetters: response.thankYouLetters || [],
            },
            Object.prototype.hasOwnProperty.call(response, "generalLedgers")
          );
          setIsValid(true);
          setCanConnect(false);
          setIsLoading(false);
          onValidationChange(true);
        } else {
          setIsValid(false);
          setIsLoading(false);
          onValidationChange(false);
          setFeedback(
            response?.message || "An error ocurred. Try again later."
          );
        }
      })
      .catch(err => {
        setIsValid(false);
        setIsLoading(false);
        onValidationChange(false);
        setFeedback(err.message);
      });
  };

  return (
    <div>
      <p>
        Enter a new DonorPerfect API key below. Verify the key before saving.
        Auto sync must be disabled to update the API key.
      </p>
      <p>
        Current API key: <strong>{maskApiKey(currentApiKey)}</strong>
      </p>
      <p className={styles.wizardHeading}>New DonorPerfect API Key</p>
      <ApiKeyInput
        feedback={feedback}
        isValid={isValid}
        isConnected={false}
        isLoading={isLoading}
        apiKeyValue={apiKeyValue}
        canConnect={canConnect}
        connectButtonLabel="Verify API Key"
        inputType="password"
        setApiKeyValue={handleApiKeyChange}
        setCanConnect={setCanConnect}
        setIsValid={setIsValid}
        setFeedback={setFeedback}
        onConnectKey={verifyApiKey}
      />
    </div>
  );
};

export default ApiKeySettingsStep;
